<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { computed } from "vue"

import LabeledSection from "@/components/LabeledSection.vue"
import LoanListItem from "@/components/LoanListItem.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"
import { itemLoanPath } from "@/routes"
import type { LoanSummary } from "@/types/models"

interface Props {
  lent: LoanSummary[]
  borrowed: LoanSummary[]
}

const props = defineProps<Props>()

const { title } = useNavbar({ title: "Loans" })

const totalCount = computed(() => props.lent.length + props.borrowed.length)

/** Sort overdue loans to the top, then by most recent. */
function overdueFirst(loans: LoanSummary[]): LoanSummary[] {
  // eslint-disable-next-line unicorn/no-array-sort -- toSorted requires es2023 lib
  return [...loans].sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1
    return 0 // preserve backend sort (by_recent) within each group
  })
}

const sortedLent = computed(() => overdueFirst(props.lent))
const sortedBorrowed = computed(() => overdueFirst(props.borrowed))

function markReturned(loanId: string) {
  const loan = [...props.lent, ...props.borrowed].find((l) => l.id === loanId)
  if (!loan) return
  router.patch(itemLoanPath(loan.item_id, loanId), { return: "1" }, { preserveScroll: true })
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <div class="flex items-center justify-between">
      <h2 class="text-highlighted text-lg font-semibold">Loans</h2>
      <span v-if="totalCount" class="text-muted text-sm">{{ totalCount }} active</span>
    </div>

    <!-- Lent Out -->
    <LabeledSection v-if="sortedLent.length" title="Lent Out" :count="sortedLent.length">
      <LoanListItem
        v-for="loan in sortedLent"
        :key="loan.id"
        :loan="loan"
        show-person
        show-return
        @returned="markReturned"
      />
    </LabeledSection>

    <!-- Borrowed -->
    <LabeledSection v-if="sortedBorrowed.length" title="Borrowed" :count="sortedBorrowed.length">
      <LoanListItem
        v-for="loan in sortedBorrowed"
        :key="loan.id"
        :loan="loan"
        show-person
        show-return
        @returned="markReturned"
      />
    </LabeledSection>

    <UEmpty
      v-if="!lent.length && !borrowed.length"
      :icon="ICON.loan"
      title="No active loans"
      description="Record a loan from any item page to track what you've lent out or borrowed."
    />
  </NarrowPage>
</template>
